import { LazyQueryExecFunction, useLazyQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from '../../components/Button';
import { MobileSuccessCheckout } from '../../components/MobileSuccessCheckout';
import { GET_MEMBERSHIP, TRACK_CHECKOUT } from '../../services/ApolloClient';
import { ErrorsService } from '../../services/ErrorsService';
import { CheckoutAppSource } from '../../types/products';
import { cleanLocalStorageItems } from '../../utils';
import { BrowserPlatform, getBrowserPlatform } from '../../utils/getBrowser';
import { EMembershipStatus, TMembershipData } from '../MyAccount/components/types';
import './SuccessCheckout.scss';

const MEMBERSHIP_POLL_INTERVAL_MS = 1000;
const MEMBERSHIP_POLL_MAX_ATTEMPTS = 10;
const READY_MEMBERSHIP_STATUSES: ReadonlyArray<EMembershipStatus> = [
  EMembershipStatus.active,
  EMembershipStatus.trial,
];

type MembershipQueryData = { membership: TMembershipData | null };
type MembershipQueryVars = { forceUpdate?: boolean };
type GetMembershipFn = LazyQueryExecFunction<MembershipQueryData, MembershipQueryVars>;

async function waitForMembership(
  getMembership: GetMembershipFn,
  isCancelled: () => boolean = () => false,
): Promise<boolean> {
  for (let attempt = 0; attempt < MEMBERSHIP_POLL_MAX_ATTEMPTS; attempt += 1) {
    if (isCancelled()) return false;
    if (attempt > 0) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => {
        setTimeout(resolve, MEMBERSHIP_POLL_INTERVAL_MS);
      });
      // Re-check after the delay: the customer may have navigated away while we slept, and
      // the final attempt sends forceUpdate, which costs a live provider call and a write.
      if (isCancelled()) return false;
    }
    try {
      // forceUpdate will call provider directly, we keep at last-resort and to avoid race conditions.
      const isLastAttempt = attempt === MEMBERSHIP_POLL_MAX_ATTEMPTS - 1;

      // eslint-disable-next-line no-await-in-loop
      const result = await getMembership({ variables: { forceUpdate: isLastAttempt } });
      const status = result?.data?.membership?.status;
      if (status && READY_MEMBERSHIP_STATUSES.includes(status)) return true;
    } catch (e) {
      ErrorsService.message(
        `[SuccessCheckout] Membership polling attempt failed: ${
          e instanceof Error ? e.message : String(e)
        }`,
        { level: 'warning', extra: { error: e, attempt } },
      );
    }
  }
  return false;
}

function SuccessCheckout() {
  const [trackCheckout] = useLazyQuery(TRACK_CHECKOUT);
  const [getMembership] = useLazyQuery<MembershipQueryData, MembershipQueryVars>(GET_MEMBERSHIP, {
    fetchPolicy: 'no-cache',
  });
  const [searchParams] = useSearchParams();
  const paymentMethod = searchParams.get('payment_method');
  const currentPlatform = getBrowserPlatform();
  const [isTrackingComplete, setIsTrackingComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const validAppSources = [CheckoutAppSource.IOS, CheckoutAppSource.ANDROID];
  const appSource = searchParams.get('app')?.toLowerCase() as CheckoutAppSource;

  const isFromValidAppSource = validAppSources.includes(appSource);

  const redirectUrl = `${window.location.origin}/app/change-stage/${window.location.search}`;
  const isFromMobileRedirect =
    isFromValidAppSource && currentPlatform === BrowserPlatform.MOBILE_WEB;

  useEffect(() => {
    let cancelled = false;
    setHasError(false);

    (async () => {
      if (retryCount === 0) {
        try {
          cleanLocalStorageItems(['coupon']);
          const payload = JSON.stringify({
            payment_method: paymentMethod,
          });
          await trackCheckout({
            variables: {
              payload,
            },
          });
        } catch (e) {
          ErrorsService.message(
            `[SuccessCheckout] Checkout tracking failed: ${
              e instanceof Error ? e.message : String(e)
            }`,
            { extra: { error: e } },
          );
        }
      }

      // Wait for the membership to actually be active before handing the customer to
      // /app/change-stage/. WordPress guards that route on access, so leaving while the
      // provider webhook is still in flight bounced returning customers to the join page
      // seconds after a successful checkout (SYS-20704). The final poll sends forceUpdate,
      // which re-reads the provider instead of the cached membership row.
      const found = await waitForMembership(getMembership, () => cancelled);
      if (cancelled) return;
      if (!found) {
        ErrorsService.message('[SuccessCheckout] Membership not found after polling timeout', {
          extra: { paymentMethod, attempts: MEMBERSHIP_POLL_MAX_ATTEMPTS, retryCount },
        });
        setHasError(true);
        return;
      }

      setIsTrackingComplete(true);

      // For web we still redirect to the web app
      if (!isFromMobileRedirect) {
        window.location.href = redirectUrl;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [retryCount, getMembership, isFromMobileRedirect, paymentMethod, redirectUrl, trackCheckout]);

  if (isFromMobileRedirect && isTrackingComplete) {
    return <MobileSuccessCheckout webRedirectUrl={redirectUrl} />;
  }

  if (hasError) {
    return (
      <div className="success-checkout-loading">
        <div className="success-checkout-loading__content">
          <p className="success-checkout-loading__message">
            We&apos;re having trouble setting up your subscription
          </p>
          <p className="success-checkout-loading__description">
            This usually takes just a moment. Please try again, or contact support if the issue
            persists.
          </p>
          <Button
            label="Try again"
            styleKey="dark"
            onClick={() => setRetryCount(count => count + 1)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="success-checkout-loading">
      <div className="success-checkout-loading__content">
        <div className="success-checkout-loading__spinner" />
        <p className="success-checkout-loading__message">Setting up your subscription…</p>
      </div>
    </div>
  );
}

export default SuccessCheckout;
